/**
 * Accueil « pièces en attente » banner — registered as
 * `extranet.pendingPiecesBanner` (fiche 22080): a warning `Alert` with a bell
 * « Vous avez **{n} pièces** en attente de dépôt », a secondary line and a
 * « Voir la liste » link to `/pieces-attendues`.
 *
 * The count is `WS2_EA_CTR_PMQ.NB`, read through `usePendingPiecesCount()` —
 * the same cached query as the header bell, so it is fetched once. The banner
 * renders nothing while the count is 0 (loading, error or no pending piece).
 * `count` presets the value (admin preview).
 *
 * Configurable-ready (texts in `pendingPiecesBannerConfig.ts`): the headline
 * is the component title (`resolveCardTitle` — `setTitle`, `{count}`
 * placeholder), the secondary line is the info item `description` (rename /
 * hide / tooltip via the `items` — or engine `fields` — bucket), the link is
 * the action `viewList` (`setActionLabel` / `hideAction`); the base config is
 * published with `usePublishLayoutConfig`.
 */

import { useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Bell, ChevronRight } from 'lucide-react';

import { Button } from '@components/base/actions';
import { Icon, Text, Tooltip } from '@components/base/display';
import { Alert } from '@components/base/feedback';
import { HStack, VStack } from '@components/base/layout';
import type { ActionsConfig } from '@components/integrated/entity-form';
import {
  applyItemOverrides,
  buildPublishedConfigFromInfoItems,
  resolveCardTitle,
  usePublishLayoutConfig,
  type InfoItemDescriptor,
  type LayoutOverrides,
  type PublishedLayoutConfig,
} from '@components/shared/configurable';

import { usePendingPiecesCount } from '../../hooks/usePendingPiecesCount';
import { extranetPath } from '../../navigation';
import { useSpaLink } from '../useSpaLink';
import {
  COUNT_PLACEHOLDER,
  PENDING_PIECES_BANNER_ACTION,
  PENDING_PIECES_BANNER_ID,
  PENDING_PIECES_BANNER_ITEMS,
  PENDING_PIECES_BANNER_TITLE,
  pendingPiecesCountText,
} from './pendingPiecesBannerConfig';

/** Handler name published for the navigation action (documentation only). */
const NAVIGATE_ACTION_HANDLER = 'extranet-navigate';

/** Published base config: the title, the text items and the link. */
const PUBLISHED_ITEMS: InfoItemDescriptor[] = PENDING_PIECES_BANNER_ITEMS.map((item) => ({
  key: item.key,
  label: item.label,
  order: item.order,
}));
const PUBLISHED_ACTIONS: ActionsConfig = {
  custom: [
    {
      id: PENDING_PIECES_BANNER_ACTION.key,
      label: PENDING_PIECES_BANNER_ACTION.label,
      handler: NAVIGATE_ACTION_HANDLER,
      variant: 'ghost',
    },
  ],
};
const PUBLISHED_CONFIG: PublishedLayoutConfig = {
  ...buildPublishedConfigFromInfoItems(PUBLISHED_ITEMS, PENDING_PIECES_BANNER_TITLE),
  actions: PUBLISHED_ACTIONS,
};

export interface PendingPiecesBannerProps {
  /** Preset count (admin preview) — when set, the shared counter is ignored. */
  count?: number;
  /** Injected by `withComponentRules` — never passed by hand. */
  _layoutOverrides?: LayoutOverrides;
}

/** Headline: the title template with its `{count}` fragment in bold. */
function renderHeadline(template: string, count: number): ReactNode {
  const countText = pendingPiecesCountText(count);
  const index = template.indexOf(COUNT_PLACEHOLDER);
  if (index === -1) return template;
  const before = template.slice(0, index);
  const after = template.slice(index + COUNT_PLACEHOLDER.length).split(COUNT_PLACEHOLDER).join(countText);
  return (
    <>
      {before}
      <Text as="span" size="sm" weight="semibold" data-testid="pending-pieces-banner-count">
        {countText}
      </Text>
      {after}
    </>
  );
}

export function PendingPiecesBanner({
  count: presetCount,
  _layoutOverrides,
}: Readonly<PendingPiecesBannerProps>) {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const spaLink = useSpaLink();
  const pending = usePendingPiecesCount();

  usePublishLayoutConfig(PENDING_PIECES_BANNER_ID, PUBLISHED_CONFIG);

  const itemOverrides = _layoutOverrides?.items ?? _layoutOverrides?.fields;
  const items = useMemo(
    () =>
      applyItemOverrides(PENDING_PIECES_BANNER_ITEMS, itemOverrides)
        .filter((item) => item.visible !== false)
        .sort((a, b) => a.order - b.order),
    [itemOverrides],
  );

  const count = presetCount ?? (pending.error ? 0 : pending.count);
  if (count <= 0) return null;

  const title = resolveCardTitle(PENDING_PIECES_BANNER_TITLE, _layoutOverrides);
  const actionOverride = _layoutOverrides?.actions?.[PENDING_PIECES_BANNER_ACTION.key];
  const actionVisible = actionOverride?.visible ?? true;
  const actionLabel = actionOverride?.label ?? PENDING_PIECES_BANNER_ACTION.label;
  const href = extranetPath(tenantSlug, PENDING_PIECES_BANNER_ACTION.to);

  return (
    <Alert
      variant="warning"
      icon={<Icon icon={Bell} size="md" color="warning" />}
      data-testid="extranet-pending-pieces-banner"
    >
      <HStack gap={4} align="center" justify="between" wrap>
        <VStack gap={1} className="min-w-0">
          <Text size="sm" weight="medium" color="default">
            {renderHeadline(title, count)}
          </Text>
          {items.map((item) => {
            const text = (
              <Text key={item.key} size="xs" color="muted" data-item-key={item.key}>
                {item.label}
              </Text>
            );
            return item.tooltip === undefined ? (
              text
            ) : (
              <Tooltip key={item.key} content={item.tooltip}>
                {text}
              </Tooltip>
            );
          })}
        </VStack>
        {actionVisible && (
          <Button
            variant="ghost"
            size="sm"
            rightIcon={<Icon icon={ChevronRight} size="xs" />}
            data-action-key={PENDING_PIECES_BANNER_ACTION.key}
            onClick={spaLink(href)}
          >
            {actionLabel}
          </Button>
        )}
      </HStack>
    </Alert>
  );
}
